import React, { type FC, useState, useEffect, type PropsWithChildren } from 'react';

import { getColor } from '@lucid/theme';
import { withImage } from '@lucid/hooks';

import reactHTMLParser from '../../utils/reactHTMLParser';
import Box from '../box/box';
import Container from '../container/container';
import Heading from '../heading/heading';
import ImageWithPlaceholder from '../image/image-with-placeholder';
import Link from '../link/link';
import VideoPlayer from '../video/video-player/video-player';
import type { Lucid } from '../../types/types';
import VideoCarousel from '../video/video-carousel/video-carousel';

export interface FeaturedVideoProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The CTA button link and text. */
  cta?: Lucid.Directus.Generated.Links;
  /** The background color for the background shown behind the video. */
  background?: string;
  /** The html (usually bullet points) for the FeaturedVideo component. */
  bullets?: string[];
  html?: string;
  /** The heading for the FeaturedVideo component. */
  heading: string;
  /** Sets the text variant for the Section Title */
  headingTextVariant?: Lucid.Theme.Component.Typography.VariantKeys;
  /** Option to change the text background color */
  textBackgroundColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
  /** The videos for the FeaturedVideo component */
  videos: Lucid.Directus.Generated.Video[];
}

const FeaturedVideo: FC<PropsWithChildren<FeaturedVideoProps>> = ({
  className,
  cta,
  background = 'transparent',
  // bullets,
  html,
  heading,
  headingTextVariant = 'heading2XL',
  textBackgroundColor,
  videos,
  ...rest
}) => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  useEffect(() => {
    if (videos.length > 1) {
      const bullets = document.querySelectorAll('#bullet-box > ul > li');

      bullets.forEach((bullet, idx) => {
        (bullet as HTMLLIElement).style.visibility = idx !== currentVideoIndex ? 'hidden' : 'visible';
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVideoIndex]);

  return (
    <Box
      {...rest}
      css={theme => {
        const currentColor = getColor({ color: textBackgroundColor, theme });

        return [
          {
            label: 'featuredVideo',
            margin: `${theme.space.s40}px 0`,
            padding: `${theme.space.s80}px 0`,
            background: `linear-gradient(${theme.colors.coolGrey[100]} 60%, ${background} 60%)`,
            [theme.breakpoints.bp1220.min]: {
              background: `linear-gradient(90deg, ${theme.colors.coolGrey[100]} 53.5%, ${background} 53.5%)`,
            },
          },
          textBackgroundColor && {
            background: `linear-gradient(${currentColor} 60%, ${background} 60%)`,
            [theme.breakpoints.bp1220.min]: {
              background: `linear-gradient(90deg, ${currentColor} 53.5%, ${background} 53.5%)`,
            },
          },
        ];
      }}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
    >
      <Container
        css={theme => {
          const currentColor = getColor({ color: textBackgroundColor, theme });

          return [
            {
              marginBottom: theme.space.s48,
              background: theme.colors.coolGrey[100],
              [theme.breakpoints.bp1220.min]: {
                marginBottom: 0,
                background: 'transparent',
              },
            },
            textBackgroundColor && {
              background: currentColor,
            },
          ];
        }}
      >
        <Box
          css={theme => ({
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'center',
            [theme.breakpoints.bp1220.min]: {
              justifyContent: 'space-between',
            },
          })}
        >
          <Box
            css={theme => ({
              marginBottom: theme.space.s32,
              flex: '1 1 100%',
              [theme.breakpoints.bp1220.min]: {
                marginBottom: 0,
                flex: '0 1 40%',
              },
            })}
          >
            <Heading
              as="h2"
              css={theme => ({
                color: 'white',
                marginBottom: theme.space.s32,
              })}
              variant={headingTextVariant}
            >
              {heading}
            </Heading>
            {html && (
              <Box
                css={theme => [theme.shared.htmlParserStyles, { color: 'white', marginLeft: theme.space.s48 }]}
                id="bullet-box"
              >
                {reactHTMLParser(html)}
              </Box>
            )}
            {cta && cta.final_url && (
              <Box
                css={{
                  label: 'videoNavigationContainer',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Link
                  displayAsButton
                  aria-label={`${heading} - ${cta.default_link_text}`}
                  css={theme => ({
                    width: '100%',
                    [theme.breakpoints.bp500.min]: {
                      width: 'auto',
                    },
                  })}
                  href={cta.final_url}
                  variant="secondary"
                >
                  {cta.default_link_text}
                </Link>
              </Box>
            )}
          </Box>
          {videos && videos.length > 1 ? (
            // TODO: Not yet updated for Directus
            <VideoCarousel
              animationTiming={600}
              css={theme => ({
                marginBottom: `-${theme.space.s80}px`,
                [theme.breakpoints.bp1220.min]: {
                  marginBottom: 0,
                },
              })}
              parentVideoIndex={currentVideoIndex}
              setParentVideoIndex={setCurrentVideoIndex}
              variant="featuredVideo"
              videos={videos}
            />
          ) : (
            <Box
              css={theme => ({
                marginBottom: `-${theme.space.s80}px`,
                flex: '1 1 100%',
                [theme.breakpoints.bp1220.min]: {
                  marginBottom: 0,
                  flex: '0 1 55%',
                },
              })}
            >
              {/* TODO we should have a fallback image for all videos probably */}
              <VideoPlayer
                controls
                // TODO: verify language
                description={videos?.[0]?.video_file?.description}
                placeholder={() =>
                  videos[0]?.preview_image ? (
                    <ImageWithPlaceholder
                      alt={videos[0]?.preview_image.translations?.[0]?.alt || ''}
                      src={withImage(videos[0]?.preview_image)}
                    />
                  ) : null
                }
                playing={false}
                url={videos[0]?.external_url ?? videos[0]?.video_file?.filename_disk}
              />
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export { FeaturedVideo };
export default FeaturedVideo;
